import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import Button from '~/components/Button';
import Text from '~/components/Text';
import { INVOICE_LIST_ROUTE, ADD_INVOICE_ROUTE } from '~/constants/routes';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import InvoiceTable from '~/routes/User/Invoice/InvoiceTable';
import { InvoiceListContext } from '~/routes/User/Invoice/hooks/useInvoiceListContext';
import { useInvoiceList } from '~/routes/User/Invoice/hooks/useInvoiceList';
import AnimatingDots from '~/components/AnimatingDots';
import DeleteInvoice from '~/routes/User/Invoice/DeleteInvoice';
import SideModal from '~/components/SideModal';
import InvoiceDetails from '~/routes/User/Invoice/InvoiceDetails';
import EmptyInvoiceList from '~/routes/User/Invoice/EmptyInvoiceList';
import InvoiceList from '~/routes/User/Invoice/InvoiceList';

import { MOBILE_QRY } from '~/config';

const Wrapper = styled.div`
  padding: 16px;
  border: 1px solid var(--border1);
  border-radius: 4px;
  background: #fff;

  ${MOBILE_QRY} {
    padding: 24px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const RecentInvoices = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  const handleInvoicePageRedirect = () => {
    navigate(INVOICE_LIST_ROUTE);
  };

  const {
    isLoading,
    listData,
    listIds,
    deleteInvoiceId,
    setDeleteInvoiceId,
    onEditListInvoice,
    viewInvoiceId,
    handleStatusChange,
    setViewInvoiceId,
  } = useInvoiceList();

  function onOpenDeleteInvoiceModal(invoiceUuid: string) {
    setDeleteInvoiceId(invoiceUuid);
  }

  function onOpenViewInvoiceModal(invoiceUuid: string) {
    setViewInvoiceId(invoiceUuid);
  }

  function onEditInvoice(invoiceUuid: string) {
    navigate(`${ADD_INVOICE_ROUTE}?editUuid=${invoiceUuid}`);
  }

  return (
    <Wrapper>
      <Header>
        <Text size="smd" className="bold">
          Recent Invoices
        </Text>

        <Button size="sm" onClick={handleInvoicePageRedirect} variant="secondary">
          View All
        </Button>
      </Header>

      <DeleteInvoice
        invoiceUuid={deleteInvoiceId}
        handleClose={() => {
          setDeleteInvoiceId(null);
        }}
      />

      <InvoiceListContext.Provider
        value={{
          listIds,
          listData,
          onOpenDeleteInvoiceModal,
          onEditInvoice,
          onEditListInvoice,
          onOpenViewInvoiceModal,
          handleStatusChange,
          maxListItems: 3,
          renderFilters: false,
        }}
      >
        <SideModal
          size="sm"
          isOpen={viewInvoiceId !== null}
          onClose={() => {
            setViewInvoiceId(null);
          }}
          children={<InvoiceDetails invoiceUuid={viewInvoiceId} />}
          heading={{ text: 'Invoice Details' }}
        />

        {!isLoading && listIds.length === 0 && <EmptyInvoiceList />}

        {listIds.length > 0 && <>{isDesktop ? <InvoiceTable /> : <InvoiceList />}</>}
      </InvoiceListContext.Provider>

      {isLoading && (
        <div className="full-width center">
          <AnimatingDots />
        </div>
      )}
    </Wrapper>
  );
};

export default RecentInvoices;
